/**
 * View component for /admin/authoring/courses/:courseId
 *
 * Displays a single course from the 'byId' map in the course reducer
 * as defined by the 'selected' property
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';

// import third-party libraries
import classNames from 'classnames';
import renderHTML from 'react-render-html';
import { DateTime } from 'luxon';

// import actions
import * as courseActions from '../courseActions';
import * as resourceLinkActions from '../../resourceLink/resourceLinkActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';
import * as contentModuleActions from '../../contentModule/contentModuleActions';

// import global components
import AlertModal from '../../../global/components/modals/AlertModal.js.jsx';
import TrainingManagementLayout from '../../admin/components/TrainingManagementLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from "../../../global/components/routing/Breadcrumbs.js.jsx";
import VideoPlayer from "../../../global/components/helpers/VideoPlayer.js.jsx";
import { SelectFromArray } from '../../../global/components/forms';

// import components
import AdminCMQuickListItem from '../../contentModule/components/AdminCMQuickListItem.js.jsx';
import AdminResourceLinkListItem from '../../resourceLink/components/AdminResourceLinkListItem.js.jsx';
import ResourceLinkModal from '../../resourceLink/components/ResourceLinkModal.js.jsx';

// import utilities
import richContentUtils from '../../../global/utils/richContentUtils';
import videoUtils from "../../../global/utils/videoUtils.js";

// import course css modules
import courseStyles from '../courseModuleStyles.css';

class AdminSingleCourse extends Base {
  constructor(props) {
    super(props);
    this.state = {
      confirmSendEmailsModalOpen: false
      , emailStatusType: 'info'
      , emailStatusMessage: ''
      , createLinkModalOpen: false
      , sendingEmails: false
      , status: ''
    }
    this._bind(
      '_handleStatusChange'
      , '_saveStatusChange'
    )
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(courseActions.fetchSingleIfNeeded(match.params.courseId)).then((action) => {
      dispatch(contentModuleActions.fetchListIfNeeded("_id", action.item._contentModules));
      dispatch(sponsorActions.fetchSingleIfNeeded(action.item._organization));
      dispatch(studyActions.fetchSingleIfNeeded(action.item._group));
    });
    dispatch(resourceLinkActions.fetchListIfNeeded('_course', match.params.courseId));
  }

  componentWillReceiveProps(nextProps) {
    const { dispatch, match } = this.props;
    dispatch(resourceLinkActions.fetchListIfNeeded('_course', match.params.courseId));
  }

  _handleStatusChange(e) {
    let newState = _.update( this.state, e.target.name, function() {
      return e.target.value;
    });
  }

  _saveStatusChange() {
    console.log("_saveStatusChange")
    if(!this.state.status) {
      alert("You must select a status");
    } else {
      const { dispatch, courseStore } = this.props;
      let course = _.cloneDeep(courseStore.selected.getItem());
      course.status = this.state.status;
      dispatch(courseActions.sendUpdateCourse(course)).then((action) => {
        if(action.success) {
          this.setState({
            changePublishStatus: false
            , status: ''
          });
        } else {
          // console.log("Response Error:");
          // console.log(action);
          alert(`ERROR: ${action.error}`);
        }
      });
    }
  }

  render() {
    const {
      courseStore
      , location
      , match
      , resourceLinkStore
      , sponsorStore
      , studyStore
      , contentModuleStore
    } = this.props;

    /**
     * use the selected.getItem() utility to pull the actual course object from the map
     */
    const selectedCourse = courseStore.selected.getItem();
    const selectedSponsor = sponsorStore.selected.getItem();
    const selectedStudy = studyStore.selected.getItem();
    const contentModules = selectedCourse && selectedCourse._contentModules ? contentModuleStore.util.getList("_id", selectedCourse._contentModules) : [];
    const sortedContentModules = contentModules && contentModules.length > 0 ? contentModules.sort((a,b) => selectedCourse._contentModules.indexOf(a._id) - selectedCourse._contentModules.indexOf(b._id)) : []
    const resourceLinks = resourceLinkStore.util.getList('_course', match.params.courseId);

    const isEmpty = (
      !selectedCourse
      || !selectedCourse._id
      || courseStore.selected.didInvalidate
    );

    const noLinks = (
      !resourceLinks
      || resourceLinks.length < 1
    )

    let status = classNames(
      'status-pill'
      , { '-published': selectedCourse && selectedCourse.status === 'published'}
    )
    return (
      <TrainingManagementLayout>
        <div className="flex">
          <section className="section">
            <div className="yt-container">
              <Breadcrumbs links={location.state.breadcrumbs} />
              { isEmpty ?
                (courseStore.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
                :
                <div style={{ opacity: courseStore.isFetching ? 0.5 : 1 }}>
                  <div className="yt-row with-gutters">
                    <div className="yt-col full s_60 m_70">
                      <div className="content-container">
                        <div className="yt-row space-between">
                          <p><strong>General Course Info</strong></p>
                          <div>
                            <Link className="yt-btn x-small info" to={`${match.url}/update-course`}>Update general info</Link>
                          </div>
                        </div>
                        <hr/>
                        <h2>
                          <small><strong>Title:</strong></small><br/>
                          {selectedCourse.title}
                        </h2>
                        <small><strong>Trailer:</strong></small>
                        <div className="yt-col full m_60">
                          <VideoPlayer
                            isAutoPlay={false}
                            onVideoComplete={() => console.log('finished play')}
                            playerId={selectedCourse._id + '_trailer'}
                            playlist={videoUtils.returnPlaylist(selectedCourse.trailerVidPath, selectedCourse.trailerPosterPath, null)}
                          />
                        </div>
                        <small><strong>Sponsor:</strong></small>
                        <p> {selectedSponsor ? selectedSponsor.name : ""} </p>
                        <small><strong>Study:</strong></small>
                        <p>  {selectedStudy ? selectedStudy.name : ""} </p>
                        <small> <strong>Summary:</strong> </small>
                        <p>{selectedCourse.summary} </p>
                      </div>
                    </div>
                    <div className="yt-col full s_40 m_30">
                      <div className="content-container">
                        <p><strong>Status</strong></p>
                        <hr/>
                        <Link className="yt-btn x-small warning" to={`/admin/audit-logs/course/${match.params.courseId}`}>
                          Object History &rarr;
                        </Link>
                        <hr/>
                        <p>
                          Created on {DateTime.fromISO(selectedCourse.created).toLocaleString(DateTime.DATETIME_FULL)}
                          { selectedCourse._createdBy && userMap[selectedCourse._createdBy] ?
                            <span> by
                              <Link to={`/admin/users/${selectedCourse._createdBy}`} target="_blank"> {userMap[selectedCourse._createdBy].username}</Link>
                            </span>
                            : null
                          }
                        </p>
                        <p><small><em>Last updated {DateTime.fromISO(selectedCourse.updated).toLocaleString(DateTime.DATETIME_FULL)}</em></small></p>
                        <br/>
                        <p>Course's publish status is:</p>
                        { this.state.changePublishStatus ?
                          <div>
                            <SelectFromArray
                              items={['draft','published', 'archived']}
                              change={this._handleStatusChange}
                              name="status"
                              value={selectedCourse.status}
                            />
                            <div>
                              <button className="yt-btn x-small danger link" onClick={()=> this.setState({changePublishStatus: false})}>cancel</button>
                              <button className="yt-btn x-small info" onClick={()=> this._saveStatusChange()}>save</button>
                            </div>
                          </div>
                          :
                          <div>
                            <div className={status}>{_.startCase(selectedCourse.status).toUpperCase()}</div>
                            <button className="yt-btn x-small link danger" onClick={()=> this.setState({changePublishStatus: true})}>change</button>
                          </div>
                        }
                        
                      </div>
                      <div className="content-container">
                        <p><strong>Content Modules included in this course</strong></p>
                        <hr/>
                        <div className="-quick-list">
                          { sortedContentModules && sortedContentModules.length > 0 ?
                            sortedContentModules.map((contentModule, i) =>
                              <AdminCMQuickListItem
                                course={selectedCourse}
                                key={contentModule._id + i}
                                contentModule={contentModule}
                              />
                            )
                            :
                            null
                          }
                        </div>
                        <Link to={'/admin/authoring/content-modules/new'} target="_blank" className="yt-btn link x-small success"><i className="fas fa-plus"/> Create new module</Link>
                      </div>
                      <div className="content-container">
                        <p><strong>Resource links</strong></p>
                        <hr/>
                        <div className="resource-links">
                          { noLinks ?
                            <em> No resource links yet </em>
                            :
                            resourceLinks.map((rLink, i) =>
                              <AdminResourceLinkListItem
                                dispatch={this.props.dispatch}
                                key={i}
                                refKey="_course"
                                refId={rLink._course}
                                resourceLink={rLink}
                              />
                            )
                          }
                        </div>
                        <button className="yt-btn x-small success link" onClick={() => this.setState({createLinkModalOpen: true })}><i className="fas fa-plus"/> Add Resource</button>
                      </div>
                    </div>
                  </div>
                  <ResourceLinkModal
                    closeModal={() => this.setState({createLinkModalOpen: false})}
                    dispatch={this.props.dispatch}
                    formType="Create"
                    isOpen={this.state.createLinkModalOpen}
                    refId={selectedCourse._id}
                    refKey="_course"
                    resourceLink={resourceLinkStore.defaultItem}
                  />
                  <AlertModal
                    alertMessage={this.state.emailStatusMessage}
                    alertTitle={this.state.emailStatusTitle}
                    closeAction={() => this.setState({emailStatusModal: false})}
                    confirmAction={() => this.setState({emailStatusModal: false})}
                    confirmText="Got it"
                    isOpen={this.state.emailStatusModal}
                    type={this.state.emailStatusType}
                  />
                </div>
              }
            </div>
          </section>
        </div>
      </TrainingManagementLayout>
    )
  }
}

AdminSingleCourse.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    courseStore: store.course
    , resourceLinkStore: store.resourceLink
    , sponsorStore: store.sponsor
    , studyStore: store.study
    , contentModuleStore: store.contentModule
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(AdminSingleCourse)
);



// WEBPACK FOOTER //
// modules/course/views/AdminSingleCourse.js.jsx